/**
 * Host Global - Super Admin Early & Late Checkout Settings & Requests
 * Configure early checkout fee and refund; late checkout fee; view early checkout history
 */

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { superAdminAPI } from "utils/api";
import Icon from "@mui/material/Icon";

function EarlyCheckoutManagement() {
  const [tabValue, setTabValue] = useState(0); // 0 = Settings, 1 = Requests
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Early checkout settings
  const [settings, setSettings] = useState({
    feeType: "percentage",
    feeValue: 10,
    refundDaysMin: 10,
    refundDaysMax: 14,
  });
  // Late checkout settings
  const [lateCheckoutSettings, setLateCheckoutSettings] = useState({
    feeType: "percentage",
    feeValue: 10,
  });
  // Cancellation policy settings
  const [cancellationPolicySettings, setCancellationPolicySettings] = useState({
    feeType: "percentage",
    feeValue: 10,
  });

  // History (completed early checkouts – no approval workflow)
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    if (tabValue === 0) fetchSettings();
    else fetchRequests();
  }, [tabValue]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });
      const [earlyRes, lateRes, cancellationRes] = await Promise.all([
        superAdminAPI.getEarlyCheckoutSettings(),
        superAdminAPI.getLateCheckoutSettings(),
        superAdminAPI.getCancellationPolicySettings(),
      ]);
      setSettings({
        feeType: earlyRes.feeType ?? "percentage",
        feeValue: earlyRes.feeValue ?? 10,
        refundDaysMin: earlyRes.refundDaysMin ?? 10,
        refundDaysMax: earlyRes.refundDaysMax ?? 14,
      });
      setLateCheckoutSettings({
        feeType: lateRes.feeType ?? "percentage",
        feeValue: lateRes.feeValue ?? 10,
      });
      setCancellationPolicySettings({
        feeType: cancellationRes.feeType ?? "percentage",
        feeValue: cancellationRes.feeValue ?? 10,
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
      setMessage({ type: "error", text: error.message || "Failed to load settings" });
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });
      // Only show completed early checkouts (no pending/approval workflow)
      const response = await superAdminAPI.getEarlyCheckoutRequests("completed");
      setRequests(response.requests || []);
    } catch (error) {
      console.error("Error fetching early checkout history:", error);
      setMessage({ type: "error", text: error.message || "Failed to load history" });
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshHistory = () => fetchRequests();

  const handleSaveAllSettings = async () => {
    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });
      await Promise.all([
        superAdminAPI.updateEarlyCheckoutSettings({
          feeType: settings.feeType,
          feeValue: settings.feeValue,
        }),
        superAdminAPI.updateLateCheckoutSettings(lateCheckoutSettings),
        superAdminAPI.updateCancellationPolicySettings(cancellationPolicySettings),
      ]);
      setMessage({ type: "success", text: "All settings updated successfully" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Failed to update settings" });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (n) => (n != null ? `${Number(n).toLocaleString("en-US")} EGP` : "—");

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3} display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={4}>
          <MDTypography variant="h4" fontWeight="bold" mb={1}>
            Stay Modifications Settings
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            Configure early checkout and late checkout fees; view early checkout history
          </MDTypography>
        </MDBox>

        {message.text && (
          <Alert
            severity={message.type === "error" ? "error" : "success"}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
          >
            <Tab
              label="Settings"
              icon={<Icon sx={{ fontSize: "1.2rem" }}>settings</Icon>}
              iconPosition="start"
            />
            <Tab
              label="Early Checkout History"
              icon={<Icon sx={{ fontSize: "1.2rem" }}>history</Icon>}
              iconPosition="start"
            />
          </Tabs>

          {tabValue === 0 && (
            <MDBox p={3}>
              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <MDTypography variant="h6" fontWeight="bold" mb={2}>
                    Early Checkout Fee & Refund
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" mb={3}>
                    These settings apply only to &quot;Early checkout by days&quot; (1+ days before
                    original check-out). Same-day early checkout has no fee.
                  </MDTypography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { minHeight: 44 } }}>
                        <InputLabel>Fee type</InputLabel>
                        <Select
                          value={settings.feeType}
                          label="Fee type"
                          onChange={(e) => setSettings((s) => ({ ...s, feeType: e.target.value }))}
                        >
                          <MenuItem value="percentage">Percentage of refundable amount</MenuItem>
                          <MenuItem value="fixed">Fixed amount (EGP)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label={
                          settings.feeType === "percentage" ? "Fee percentage" : "Fee amount (EGP)"
                        }
                        value={settings.feeValue}
                        onChange={(e) =>
                          setSettings((s) => ({
                            ...s,
                            feeValue: Math.max(0, Number(e.target.value) || 0),
                          }))
                        }
                        inputProps={{ min: 0, step: settings.feeType === "percentage" ? 1 : 10 }}
                        sx={{ "& .MuiInputBase-root": { minHeight: 44 } }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <MDTypography variant="h6" fontWeight="bold" mb={2}>
                    Late Checkout Fee
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" mb={3}>
                    These settings apply to same-day late checkout (staying past 12:00 PM on
                    check-out day). Fee is calculated from hours after noon based on price per
                    night. Late checkout is only available if no other reservation checks in on the
                    checkout day.
                  </MDTypography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { minHeight: 44 } }}>
                        <InputLabel>Fee type</InputLabel>
                        <Select
                          value={lateCheckoutSettings.feeType}
                          label="Fee type"
                          onChange={(e) =>
                            setLateCheckoutSettings((s) => ({ ...s, feeType: e.target.value }))
                          }
                        >
                          <MenuItem value="percentage">Percentage of hourly rate</MenuItem>
                          <MenuItem value="fixed">Fixed amount per hour (EGP)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label={
                          lateCheckoutSettings.feeType === "percentage"
                            ? "Fee percentage"
                            : "Fee per hour (EGP)"
                        }
                        value={lateCheckoutSettings.feeValue}
                        onChange={(e) =>
                          setLateCheckoutSettings((s) => ({
                            ...s,
                            feeValue: Math.max(0, Number(e.target.value) || 0),
                          }))
                        }
                        inputProps={{
                          min: 0,
                          step: lateCheckoutSettings.feeType === "percentage" ? 1 : 10,
                        }}
                        sx={{ "& .MuiInputBase-root": { minHeight: 44 } }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <MDTypography variant="h6" fontWeight="bold" mb={2}>
                    Cancellation Policy
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary" mb={3}>
                    Fee applied when a client cancels a reservation before check-in. Reason is
                    optional. The fee is deducted from the total amount; the remainder is refunded
                    within 10–14 working days.
                  </MDTypography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { minHeight: 44 } }}>
                        <InputLabel>Fee type</InputLabel>
                        <Select
                          value={cancellationPolicySettings.feeType}
                          label="Fee type"
                          onChange={(e) =>
                            setCancellationPolicySettings((s) => ({
                              ...s,
                              feeType: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="percentage">Percentage of total amount</MenuItem>
                          <MenuItem value="fixed">Fixed amount (EGP)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label={
                          cancellationPolicySettings.feeType === "percentage"
                            ? "Fee percentage"
                            : "Fee amount (EGP)"
                        }
                        value={cancellationPolicySettings.feeValue}
                        onChange={(e) =>
                          setCancellationPolicySettings((s) => ({
                            ...s,
                            feeValue: Math.max(0, Number(e.target.value) || 0),
                          }))
                        }
                        inputProps={{
                          min: 0,
                          step: cancellationPolicySettings.feeType === "percentage" ? 1 : 10,
                        }}
                        sx={{ "& .MuiInputBase-root": { minHeight: 44 } }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <MDButton
                    variant="gradient"
                    color="dark"
                    onClick={handleSaveAllSettings}
                    disabled={submitting}
                    startIcon={<Icon>save</Icon>}
                  >
                    {submitting ? "Saving..." : "Save all settings"}
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
          )}

          {tabValue === 1 && (
            <MDBox p={3}>
              <MDBox display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
                <MDTypography variant="h6" fontWeight="bold">
                  Early checkout by days – history (no approval; applied immediately)
                </MDTypography>
                <MDButton
                  size="small"
                  color="info"
                  onClick={handleRefreshHistory}
                  startIcon={<Icon>refresh</Icon>}
                >
                  Refresh
                </MDButton>
              </MDBox>
              {requests.length === 0 ? (
                <MDTypography variant="body2" color="text.secondary">
                  No completed early checkouts yet.
                </MDTypography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Reservation</TableCell>
                        <TableCell>Property</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Original check-out</TableCell>
                        <TableCell>New check-out</TableCell>
                        <TableCell>Requested at</TableCell>
                        <TableCell>Fee (from cancelled days)</TableCell>
                        <TableCell>Refund</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {requests.map((req) => (
                        <TableRow key={req.reservationId}>
                          <TableCell>{req.reservationNumber || req.reservationId}</TableCell>
                          <TableCell>
                            {req.property?.title?.en || req.property?.title || "—"}
                          </TableCell>
                          <TableCell>{req.client?.fullName || req.client?.email || "—"}</TableCell>
                          <TableCell>{formatDate(req.originalCheckOutDate)}</TableCell>
                          <TableCell>{formatDate(req.requestedCheckOutDate)}</TableCell>
                          <TableCell>{formatDate(req.requestedAt)}</TableCell>
                          <TableCell>
                            {formatCurrency(req.feeAmount ?? req.calculatedFeeAmount)}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(req.refundAmount ?? req.calculatedRefundAmount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </MDBox>
          )}
        </Card>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default EarlyCheckoutManagement;
